import React, { useState, useEffect } from "react";
import { LoanStatusDetails } from "../../interface/MembersPortal/ILoan";
import helpers from "../../helpers/index";
import SignatureWithNameModal from "../Signature/SignatureWithNameModal";

interface DisclosureStatementLoanCreditTransactionFormProps {
    loanId?: string;
    loanStatusDetails: LoanStatusDetails | null;
    onRepresentativeChange?: (signature: string, name: string) => void;
    onRepresentativePositionChange?: (position: string) => void;
}

function DisclosureStatementLoanCreditTransactionForm({ loanId, loanStatusDetails, onRepresentativeChange, onRepresentativePositionChange }: DisclosureStatementLoanCreditTransactionFormProps) {

    const [isRepSignModalOpen, setIsRepSignModalOpen] = useState(false);
    const [repSignature, setRepSignature] = useState<string>('');
    const [repFullname, setRepFullname] = useState<string>('');
    const [repPosition, setRepPosition] = useState<string>('');
    const [isHoveringRep, setIsHoveringRep] = useState(false);

    useEffect(function () {
        if (loanStatusDetails) {
            setRepSignature(loanStatusDetails.representativeSignature ?? '');
            setRepFullname(loanStatusDetails.representativeFullname ?? '');
            setRepPosition(loanStatusDetails.representativePosition ?? '');
        }
    }, [loanStatusDetails]);

    const isMember = helpers.getCookieValue('role') === 'Member';
    const showRepSignButton = !isMember && loanStatusDetails?.loanStatus === 2 && (!(repSignature && repFullname) || isHoveringRep);

    const middleInitial = loanStatusDetails?.middlename ? `${loanStatusDetails.middlename.charAt(0).toUpperCase()}.` : '';
    const fullName = [loanStatusDetails?.firstname, middleInitial, loanStatusDetails?.lastname].filter(Boolean).join(' ');
    const amountLoan = Number(loanStatusDetails?.amountLoan ?? 0);
    const term = loanStatusDetails?.term ?? 0;
    const amortization = Number(loanStatusDetails?.amortizationSchedule ?? 0);
    const interestRate = loanStatusDetails?.interestRate ?? 0;
    const serviceFee = Number(loanStatusDetails?.serviceFee ?? 0);

    const totalFinanceCharges = amortization * term - amountLoan;
    const totalDeductions = serviceFee;
    const netProceeds = amountLoan - serviceFee;

    const fmt = (val: number) =>
        val > 0 ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

    const uline = (content: React.ReactNode = '\u00A0', width = '120px') => (
        <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: width, textAlign: 'center', padding: '0 2px' }}>
            {content}
        </span>
    );

    const cb = (label: string) => (
        <span style={{ marginRight: '12px', whiteSpace: 'nowrap' }}>
            <input type="checkbox" disabled style={{ marginRight: '3px' }} />{label}
        </span>
    );

    const valCell: React.CSSProperties = {
        width: '110px',
        borderBottom: '1px solid #000',
        textAlign: 'center',
        padding: '0 4px',
        flexShrink: 0,
    };

    const twoColRow = (label: React.ReactNode, left: React.ReactNode = '\u00A0', right: React.ReactNode = '\u00A0', indent = '16px') => (
        <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '3px', marginLeft: indent, lineHeight: '1.6' }}>
            <div style={{ flex: 1 }}>{label}</div>
            <div style={{ ...valCell }}>{left}</div>
            <div style={{ ...valCell }}>{right}</div>
        </div>
    );

    return (
        <div style={{ maxWidth: '820px', margin: '0 auto', fontFamily: 'Times New Roman, serif', fontSize: '12px', padding: '32px', background: '#fff', border: '1px solid #ccc' }}>

            {/* Form No */}
            <div style={{ fontSize: '11px', marginBottom: '2px' }}>06-004</div>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                <div style={{ fontWeight: 'bold' }}>KOOPERATIBA NG MGA KAWANI</div>
                <div style={{ fontWeight: 'bold' }}>NG PAMAHALAANG PANLALAWIGAN</div>
                <div style={{ fontWeight: 'bold' }}>NG BATANGAN (KOOPBATANGAN)</div>
                <br />
                <div style={{ fontWeight: 'bold', fontSize: '13px', textDecoration: 'underline' }}>
                    DISCLOSURE STATEMENT OF LOAN/CREDIT TRANSACTION
                </div>
                <div style={{ fontSize: '11px' }}>(As required under R.A. 3765, Truth in Lending Act)</div>
            </div>

            {/* Borrower / Address */}
            <div style={{ marginBottom: '4px' }}>
                <strong>NAME OF BORROWER </strong>
                <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '400px', padding: '0 4px' }}>
                    {fullName || '\u00A0'}
                </span>
            </div>
            <div style={{ marginBottom: '14px' }}>
                <strong>ADDRESS </strong>
                <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '460px', padding: '0 4px' }}>
                    {loanStatusDetails?.address || '\u00A0'}
                </span>
            </div>

            {/* Section 1 */}
            <div style={{ marginBottom: '10px' }}>
                <strong>1. LOAN/GRANTED (Amount to be financed) </strong>
                {uline(amountLoan > 0 ? fmt(amountLoan) : undefined, '130px')}
                &nbsp; P &nbsp;
                {uline(amountLoan > 0 ? fmt(amountLoan) : undefined, '100px')}
                &nbsp;&nbsp;<strong>(A)</strong>
            </div>

            {/* Section 2 header */}
            <div style={{ marginBottom: '2px' }}>
                <strong>2. FINANCE CHARGES</strong>
            </div>

            {/* Two-column sub-header */}
            <div style={{ display: 'flex', marginBottom: '2px' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ width: '110px', textAlign: 'center', fontSize: '11px', flexShrink: 0 }}>
                    Not Deducted<br />From
                </div>
                <div style={{ width: '110px', textAlign: 'center', fontSize: '11px', flexShrink: 0 }}>
                    Deducted<br />From<br />Proceeds of Loan
                </div>
            </div>

            {/* 2a Interest */}
            <div style={{ marginLeft: '16px', marginBottom: '2px' }}>
                <div>
                    a. Interest {uline(interestRate > 0 ? `${interestRate}` : undefined, '36px')}% p.a. from{' '}
                    {uline(undefined, '60px')} to {uline(undefined, '60px')}
                    &nbsp; P {uline(undefined, '50px')} &nbsp; P {uline(undefined, '50px')}
                </div>
                <div style={{ marginTop: '2px' }}>
                    {cb('Simple')}{cb('Monthly')}{cb('Annual')}
                </div>
                <div>
                    {cb('Compound')}{cb('Quarterly')}{cb('Semi-Annual')}
                </div>
            </div>

            {/* 2b Non-Interest Charges */}
            {twoColRow('b. Non-Interest Charges')}
            {/* 2c Commitment Fee */}
            {twoColRow('c. Commitment Fee')}
            {/* 2d Guarantee Fee */}
            {twoColRow('d. Guarantee Fee')}
            {/* 2e Other charges */}
            {twoColRow(
                <span>e. Other charges incidental to the extension of<br />&nbsp;&nbsp;&nbsp;&nbsp;credit (specify)</span>
            )}

            {/* Total Finance Charges */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '8px', marginLeft: '60px' }}>
                <div style={{ flex: 1, textAlign: 'right', paddingRight: '8px' }}>Total Finance Charges</div>
                <div style={{ ...valCell }}>
                    {totalFinanceCharges > 0 ? `P ${fmt(totalFinanceCharges)}` : 'P'}
                </div>
                <div style={{ ...valCell }}>P</div>
            </div>

            {/* Section 3 */}
            <div style={{ marginBottom: '2px' }}>
                <strong>3. NON-FINANCE CHARGES</strong>
            </div>
            {twoColRow('a. Insurance Premium', 'P', 'P')}
            {twoColRow('b. Taxes')}
            {twoColRow('c. Documentary Science Stamps')}
            {twoColRow('d. Notarial Fees')}
            {twoColRow('e. Others (specify)')}

            {/* Total Non-Finance Charges */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '8px', marginLeft: '60px' }}>
                <div style={{ flex: 1, textAlign: 'right', paddingRight: '8px' }}>Total non-finance charges</div>
                <div style={{ ...valCell }}>P</div>
                <div style={{ ...valCell }}>
                    {serviceFee > 0 ? `P ${fmt(serviceFee)}` : 'P'}
                </div>
                <div style={{ marginLeft: '4px', fontWeight: 'bold' }}>(C)</div>
            </div>

            {/* Section 4 */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '4px' }}>
                <div style={{ flex: 1 }}>
                    <strong>4. TOTAL DEDUCTIONS FROM PROCEEDS OF LOANS (B plus C)</strong>
                </div>
                <div style={{ ...valCell }}>
                    {totalDeductions > 0 ? `P ${fmt(totalDeductions)}` : 'P'}
                </div>
                <div style={{ marginLeft: '4px', fontWeight: 'bold' }}>(D)</div>
            </div>

            {/* Section 5 */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                    <strong>5. NET PROCEEDS OF LOAN (A less D)</strong>
                </div>
                <div style={{ ...valCell }}>
                    {netProceeds > 0 ? `P ${fmt(netProceeds)}` : 'P'}
                </div>
                <div style={{ marginLeft: '4px', fontWeight: 'bold' }}>(E)</div>
            </div>

            {/* Section 6 */}
            <div style={{ marginBottom: '2px' }}>
                <strong>6. PERCENTAGE OF FINANCE CHARGES TO TOTAL AMOUNT FINANCED</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: '16px', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>Computed in accordance with Subsec.X301.1</div>
                <div style={{ borderBottom: '1px solid #000', minWidth: '80px', textAlign: 'center' }}>
                    {interestRate > 0 ? `${interestRate}` : '\u00A0'}
                </div>
                <span style={{ marginLeft: '2px' }}>%</span>
            </div>

            {/* Section 7 */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                    <strong>7. EFFECTIVE INTEREST RATED (Method of computation attached)</strong>
                </div>
                <div style={{ borderBottom: '1px solid #000', minWidth: '80px', textAlign: 'center' }}>
                    {interestRate > 0 ? `${interestRate}` : '\u00A0'}
                </div>
                <span style={{ marginLeft: '2px' }}>% p.a.</span>
            </div>

            {/* Section 8 */}
            <div style={{ marginBottom: '4px' }}>
                <strong>8. SCHEDULE OF PAYMENT</strong>
            </div>
            <div style={{ marginLeft: '16px', marginBottom: '4px' }}>
                <span>a. Single payment due on {uline(undefined, '120px')}</span>
                &nbsp;&nbsp;P&nbsp;{uline(undefined, '80px')}
            </div>
            <div style={{ marginLeft: '16px', marginBottom: '2px' }}>
                <span>b. Total Installment Payments Payable&nbsp;
                    {uline(term > 0 ? String(term) : undefined, '60px')} in months / year
                </span>
            </div>
            <div style={{ marginLeft: '80px', fontSize: '11px', marginBottom: '2px' }}>no. of payments</div>
            <div style={{ marginLeft: '16px', marginBottom: '10px' }}>
                <span>at P&nbsp;{uline(amortization > 0 ? fmt(amortization) : undefined, '120px')} each installment</span>
            </div>

            {/* Section 9 */}
            <div style={{ marginBottom: '4px' }}>
                <strong>9. COLLATERAL</strong>
            </div>
            <div style={{ marginLeft: '16px', marginBottom: '4px' }}>
                This loan is wholly/partly secured by (pls.check)
            </div>
            <div style={{ marginLeft: '16px', marginBottom: '4px' }}>
                {uline(undefined, '36px')}&nbsp;real estate&nbsp;&nbsp;
                {uline(undefined, '36px')}&nbsp;chattels&nbsp;&nbsp;
                {uline(undefined, '36px')}&nbsp;unsecured&nbsp;&nbsp;
                {uline(undefined, '36px')}&nbsp;deed of assign or PN
            </div>
            <div style={{ marginLeft: '16px', marginBottom: '10px' }}>
                {uline(undefined, '36px')}&nbsp;gov't. securities
            </div>

            {/* Section 10 */}
            <div style={{ marginBottom: '4px' }}>
                <strong>10. ADDITIONAL CHARGES IN CASE CERTAIN STIPULATIONS ARE NOT MET BY THE BORROWER</strong>
            </div>
            <div style={{ display: 'flex', marginLeft: '16px', marginBottom: '4px' }}>
                <div style={{ flex: 1 }}>Nature</div>
                <div style={{ width: '200px', textAlign: 'center' }}>Amount</div>
            </div>
            <div style={{ display: 'flex', marginLeft: '16px', marginBottom: '4px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>Penalty for past due accounts counted on 1<sup>st</sup> day</div>
                <div style={{ width: '200px', borderBottom: '1px solid #000' }}>&nbsp;</div>
            </div>
            <div style={{ display: 'flex', marginLeft: '16px', marginBottom: '20px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>Attorney's fee in case of suit, 10% of</div>
                <div style={{ width: '200px', borderBottom: '1px solid #000' }}>&nbsp;</div>
            </div>

            {/* Certified Correct */}
            <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                <strong>CERTIFIED CORRECT:</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                <div
                    style={{ textAlign: 'center' }}
                    onMouseEnter={() => setIsHoveringRep(true)}
                    onMouseLeave={() => setIsHoveringRep(false)}
                >
                    <div style={{ position: 'relative', width: '240px', height: '60px', display: 'inline-block' }}>
                        {repSignature && (
                            <img src={repSignature} alt="Representative Signature" style={{ width: '240px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        {showRepSignButton && (
                            <button
                                type="button"
                                onClick={() => setIsRepSignModalOpen(true)}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '11px',
                                    padding: '2px 10px',
                                    cursor: 'pointer',
                                }}
                            >Sign</button>
                        )}
                    </div>
                    <span style={{ display: 'block', borderBottom: '1px solid #000', minWidth: '240px', textAlign: 'center', padding: '0 2px' }}>
                        {repFullname || '\u00A0'}
                    </span>
                </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '11px', marginBottom: '6px' }}>
                (Signature of Creditor / Authorized<br />
                Representative Over Printed Name)
            </div>
            <div style={{ textAlign: 'right', marginBottom: '22px' }}>
                <span>Position&nbsp;</span>
                {isMember
                    ? uline(repPosition || undefined, '180px')
                    : <input
                        type="text"
                        value={repPosition}
                        onChange={(e) => {
                            setRepPosition(e.target.value);
                            onRepresentativePositionChange?.(e.target.value);
                        }}
                        style={{
                            borderBottom: '1px solid #000',
                            borderTop: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                            outline: 'none',
                            minWidth: '180px',
                            textAlign: 'center',
                            fontFamily: 'inherit',
                            fontSize: 'inherit',
                            background: 'transparent',
                        }}
                    />
                }
            </div>

            <SignatureWithNameModal
                isOpen={isRepSignModalOpen}
                title="Representative Signature"
                showPosition
                onSave={(sig, name, pos) => {
                    setRepSignature(sig);
                    setRepFullname(name);
                    setRepPosition(pos ?? '');
                    setIsRepSignModalOpen(false);
                    onRepresentativeChange?.(sig, name);
                    onRepresentativePositionChange?.(pos ?? '');
                }}
                onCancel={() => setIsRepSignModalOpen(false)}
            />

            {/* Acknowledgment */}
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', marginBottom: '16px', lineHeight: '1.6' }}>
                I ACKNOWLEDGE RECEIPT OF A COPY OF THIS STATEMENT PRIOR TO<br />
                THE CONSUMMATION OF THE CREDIT TRANSACTION AND THAT I UNDER-<br />
                STAND AND FULLY AGREE TO THE TERMS AND CONDITIONS THEREOF.
            </div>

            {/* Borrower Signature */}
            <div style={{ textAlign: 'right', marginBottom: '4px' }}>
                <div style={{ display: 'inline-block', textAlign: 'center', minWidth: '240px' }}>
                    {loanStatusDetails?.memberSignature && (
                        <img id="memberSignature" src={loanStatusDetails.memberSignature} alt="Member Signature" style={{ width: '240px', height: '60px', objectFit: 'contain', display: 'block' }} />
                    )}
                    <span style={{ display: 'block', borderBottom: '1px solid #000', fontSize: '16px', padding: '0 2px' }}>
                        {fullName || '\u00A0'}
                    </span>
                </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '11px', marginBottom: '20px' }}>
                (Signature of Borrower over Printed Name)
            </div>

            {/* Date and notice */}
            <div style={{ marginBottom: '4px' }}>
                <span>Date: </span>{uline(undefined, '160px')}
            </div>
            <div style={{ fontSize: '11px', fontStyle: 'italic' }}>
                Notice to borrower: You are entitled to a copy of this paper which you shall sign.
            </div>

        </div>
    );
}

export default DisclosureStatementLoanCreditTransactionForm;
